if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this._autoSelecting = false;
        this._mutationObserver = null;
        this._debounceTimer = null;
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);

          this.updateSelectedSwatchValue(event);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });

        const trySubscribe = () => {
          try {
            const evs = FoxTheme?.pubsub?.PUB_SUB_EVENTS || {};
            const possibleEvents = ['variantChange', 'variantUpdated', 'variantUpdatedEvent', 'variant:update'];

            possibleEvents.forEach((evName) => {
              if (evs[evName]) {
                FoxTheme.pubsub.subscribe(evs[evName], () => {
                  console.debug(`[variant-selects] received pubsub event ${evs[evName]}, calling autoSelectAvailableOptions`);
                  this.debouncedAutoSelect();
                });
              }
            });

            if (evs.variantChange) {
              FoxTheme.pubsub.subscribe(evs.variantChange, () => this.debouncedAutoSelect());
            }
            if (evs.variantUpdated) {
              FoxTheme.pubsub.subscribe(evs.variantUpdated, () => this.debouncedAutoSelect());
            }
          } catch (err) {
            console.warn('[variant-selects] cannot subscribe to FoxTheme pubsub events', err);
          }
        };

        trySubscribe();

        this._mutationObserver = new MutationObserver((mutations) => {
          if (this._debounceTimer) clearTimeout(this._debounceTimer);
          this._debounceTimer = setTimeout(() => {
            console.debug('[variant-selects] mutation observed, calling autoSelectAvailableOptions');
            this.debouncedAutoSelect();
          }, 60);
        });

        this._mutationObserver.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'selected', 'disabled', 'data-option-swatch-value', 'data-option-value-id'],
        });

        window.requestAnimationFrame(() => this.debouncedAutoSelect());
      }

      disconnectedCallback() {
        if (this._mutationObserver) this._mutationObserver.disconnect();
        if (this._debounceTimer) clearTimeout(this._debounceTimer);
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');

          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');

          if (!selectedDropdownSwatchValue) return;

          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      debouncedAutoSelect() {
        if (this._debounceTimer) clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          this.autoSelectAvailableOptions();
        }, 80); // 80ms thường đủ cho theme cập nhật xong
      }

      autoSelectAvailableOptions() {
        if (this._autoSelecting) {
          console.debug('[variant-selects] already auto-selecting, skip');
          return;
        }
        this._autoSelecting = true;
        try {
          const groups = this.querySelectorAll('.product-form__input');

          groups.forEach((group) => {
            // RADIO SWATCHS
            const radios = Array.from(group.querySelectorAll('input[type="radio"][data-option-value-id]'));
            if (radios.length) {
              const checked = radios.find((r) => r.checked);
              const isCheckedAvailable = checked && !checked.disabled && !checked.classList.contains('disabled');
              if (!isCheckedAvailable) {
                const firstAvailable = radios.find((r) => !r.disabled && !r.classList.contains('disabled'));
                if (firstAvailable) {
                  // only change if different
                  if (!firstAvailable.checked) {
                    console.debug('[variant-selects] selecting radio', firstAvailable.dataset.optionValueId);
                    firstAvailable.checked = true;
                    firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              }
            }

            const select = group.querySelector('select');
            if (select) {
              const selectedOption = select.selectedOptions[0];
              const selectedIsAvailable = selectedOption && !selectedOption.disabled && !/unavailable/i.test(selectedOption.text);
              if (!selectedIsAvailable) {
                const firstAvailable = Array.from(select.options).find((o) => !o.disabled && !/unavailable/i.test(o.text));
                if (firstAvailable && select.value !== firstAvailable.value) {
                  console.debug('[variant-selects] selecting select option', firstAvailable.value);
                  select.value = firstAvailable.value;
                  Array.from(select.options).forEach((o) => o.removeAttribute && o.removeAttribute('selected'));
                  firstAvailable.setAttribute && firstAvailable.setAttribute('selected', 'selected');
                  select.dispatchEvent(
                    new InputEvent('change', { bubbles: true })
                  );
                }
              }
            }
          });
        } catch (err) {
          console.error('[variant-selects] autoSelectAvailableOptions error', err);
        } finally {
          setTimeout(() => {
            this._autoSelecting = false;
          }, 50);
        }
      }
    }
  );
}